import * as React from 'react';
import { Button, Col, Form, Modal, Overlay, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { ApiResponse } from 'apisauce';
import { toast } from 'react-toastify';
import Api from '../../../../services/Api';
import * as urls from '../../../shared/urls';
import ViewAllButton from '../../../DocumentViewer/ViewAllButton';
import { IWorkflowItemStep } from '../../../../common/sharedTypes';
import UpdateKeywordsButton from '../../../shared/UpdateKeywordsButton/UpdateKeywordsButton';

interface IPullReportModalOwnProps {
  show: boolean;
  onHide: () => void;
  onAfterPullingIntoManagement?: () => void;
}

type IPullReportModalProps =
  IPullReportModalOwnProps;

interface IPullReportModalState {
  crashKeySearch: string;
  reportData: IManagementSearchDto;
}

interface IManagementSearchDto {
  crashDate: string;
  county: string;
  crashLocation: string;
  workflowStepId: number;
  activeSteps: IWorkflowItemStep[];
  reportId: number;
  occupants: string[];
  canPullIntoManagement: boolean;
  documentId: string;
  originalCrashKey: string;
  reportStatusId: number;
}

const REPORT_STATUS = {
  1: "New",
  2: "Active",
  3: "Completed",
  4: "Merged",
  5: "Management",
  6: "Deleted",
  7: "Supplemented"
};

class PullReportModal extends React.Component<IPullReportModalProps, IPullReportModalState> {
  constructor(props: IPullReportModalProps) {
    super(props);
    this.state = {
      crashKeySearch: '',
      reportData: null,
    };
  }

  onHide() {
    this.setState({
      reportData: null,
    });

    this.props.onHide();
  }

  handleCrashKeySearch(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()

    Api.get(urls.MANAGEMENT_CRASH_KEY_SEARCH, {
      crashKey: this.state.crashKeySearch
    })
      .then((response: ApiResponse<IManagementSearchDto>) => {
        if (response.ok) {
          this.setState({
            reportData: response.data,
          })
        } else {
          toast.error(response.data);
        }
      });
  }

  handlePullIntoManagementClick() {
    const { reportData } = this.state
    Api.post(urls.SEND_TO_MANAGEMENT_QUEUE, {
      workflowItemStepId: reportData.workflowStepId
    }).then((response: ApiResponse<any>) => {
      if (response.ok) {
        toast.success('Pulled report into management');
        if (this.props.onAfterPullingIntoManagement) {
          this.props.onAfterPullingIntoManagement();
        }
        this.onHide();
      }
      else {
        let errorMessage = 'Failed to pull into management';
        if (response.data?.Message)
          errorMessage = response.data.Message;
        toast.warn(errorMessage);
      }
    });
  }

  getPullBlockedReason(reportData: IManagementSearchDto) {
    if (reportData.activeSteps.some(s => s.assignedTo !== null)) {
      return 'Currently assigned, cannot pull';
    }

    if (!reportData.activeSteps || reportData.activeSteps.length === 0) {
      return 'No active workflow steps, cannot pull';
    }

    return 'Pending LRS, cannot pull';
  }

  render() {
    const { reportData } = this.state;

    return (
      <Modal
        show={this.props.show}
        onHide={() => this.onHide()}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>Pull Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={(e) => this.handleCrashKeySearch(e)}>
            <Row style={{ alignItems: 'flex-end' }} className="pb-3">
              <Col xs="10">
                <Form.Group controlId="crashKey">
                  <Form.Label>Crash Key</Form.Label>
                  <Form.Control
                    type="text"
                    value={this.state.crashKeySearch}
                    name="crashKey"
                    onChange={(e) => this.setState({ crashKeySearch: e.target.value })}
                    disabled={reportData !== null}
                  />
                </Form.Group>
              </Col>
              <Col
                xs="2"
                className="pl-0"
              >
                {reportData === null ? (
                  <Button
                    variant="primary"
                    style={{ width: '100%' }}
                    type="submit"
                  >
                    Search
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    onClick={(e) => { e.preventDefault(); this.setState({ reportData: null }) }}
                    style={{ width: '100%' }}
                    type="button"
                  >
                    New Search
                  </Button>
                )}
              </Col>
            </Row>
          </form>
          {reportData !== null &&
            <>
              <div
                className="d-flex justify-content-between flex-wrap px-2"
                style={{ gap: '15px' }}
              >
                <div className="px-2">
                  <h5 className="mb-1">Crash Date</h5>
                  <p className="m-0">{reportData.crashDate}</p>
                </div>
                <div className="px-2">
                  <h5 className="mb-1">County</h5>
                  <p className="m-0">{reportData.county}</p>
                </div>
                <div className="px-2">
                  <h5 className="mb-1">Crash Location</h5>
                  <p className="m-0">{reportData.crashLocation}</p>
                </div>
                <div className="px-2">
                  <h5 className="mb-1">Workflow Status</h5>
                {reportData.reportStatusId === 3 ? (
                <p className="mb-1 text-success">Completed</p>
                ) : !reportData.activeSteps || reportData.activeSteps.length === 0 ? (
                  <p className="mb-1 text-muted">No Steps</p>
                ) : (
                  reportData.activeSteps.map(s => (
                    <p key={s.id} className="mb-1">
                      {s.workflowStepStatus.name}
                      {s.assignedTo && ` to ${s.user.firstName} ${s.user.lastName}`} 
                      {" "}in {s.workflowStep.name}
                    </p>
                  ))
                )}
                </div>
                <div className="px-2">
                  <h5 className="mb-1">Report Status</h5>
                  <p className="mb-1">
                    {(REPORT_STATUS[reportData.reportStatusId] || "Unknown") + ` (${reportData.reportStatusId})`}
                  </p>
                </div>
                  <div className="px-2">
                  <h5 className="mb-1">Involved Parties</h5>
                  <ul className="pl-0">
                    {reportData.occupants.map(name => (
                      <li
                        key={name}
                      >
                        {name}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <Modal.Footer>
                {reportData.canPullIntoManagement ? (
                  <this.PullIntoManagementButton />
                ) : (
                  <OverlayTrigger
                    key="top"
                    trigger={['hover', 'focus']}
                    placement="top"
                    overlay={
                      <Tooltip id={`tooltip-pull-button`}>
                        {this.getPullBlockedReason(reportData)}
                      </Tooltip>
                    }
                  >
                    <span className="d-inline-block">
                      <this.PullIntoManagementButton />
                    </span>
                  </OverlayTrigger>
                )}
                <ViewAllButton
                  reportId={reportData.reportId}
                  text='View'
                />
                <UpdateKeywordsButton startingDocIds={reportData.documentId} 
                  classNames='btn-sm mt-10 commentButtonManagement'>                  
                </UpdateKeywordsButton>
              </Modal.Footer>
            </>
          }
        </Modal.Body>
      </Modal>
    )
  }

  PullIntoManagementButton = () => {
    return (
      <Button
        onClick={() => this.handlePullIntoManagementClick()}
        disabled={!this.state.reportData.canPullIntoManagement}
        style={{ pointerEvents: this.state.reportData.canPullIntoManagement ? 'initial' : 'none' }}
      >
        Pull Into Management
      </Button>
    )
  }
}



export default PullReportModal;